import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Appointment, CreateAppointment, UpdateAppointment } from '@salonomy/types';
import { api, ApiClientError, unwrap } from '../lib/api.js';
import { useOffline } from '../lib/offline/provider.js';
import { qk } from '../lib/query.js';
import { useToast } from '../lib/toast.js';
import { zonedDayRange } from '../lib/time.js';

export function useAppointments(date: string, tz: string | undefined) {
  return useQuery({
    queryKey: qk.appointments(date),
    enabled: Boolean(tz),
    queryFn: async (): Promise<Appointment[]> => {
      const { fromIso, toIso } = zonedDayRange(date, tz!);
      return unwrap(
        await api.GET('/v1/appointments', { params: { query: { from: fromIso, to: toIso } } }),
      );
    },
  });
}

/** Caller computes endAt + clientDisplayName from the chosen service so the optimistic block is exact. */
export type CreateApptInput = CreateAppointment & { endAt: string; clientDisplayName: string };

const TEMP_PREFIX = 'temp-';
export const isOptimistic = (a: Appointment): boolean => a.id.startsWith(TEMP_PREFIX);

function optimisticAppt(input: CreateApptInput, tempId: string): Appointment {
  const now = new Date().toISOString();
  return {
    id: tempId,
    salonId: '',
    renterId: null,
    locationId: null,
    staffId: input.staffId,
    clientId: input.clientId ?? null,
    serviceId: input.serviceId,
    clientDisplayName: input.clientDisplayName,
    startAt: input.startAt,
    endAt: input.endAt,
    status: 'booked',
    source: input.source ?? 'salon',
    priceQuoted: 0,
    depositPaid: input.depositPaid ?? 0,
    notes: input.notes ?? null,
    queueTicketId: null,
    // A diary-created booking is never a series occurrence; the server confirms null on the replace.
    seriesId: null,
    createdAt: now,
    updatedAt: now,
  };
}

export function useCreateAppointment(date: string) {
  const qc = useQueryClient();
  const { enqueue } = useOffline();
  const toast = useToast();
  const key = qk.appointments(date);

  return useMutation<
    Appointment | null,
    unknown,
    CreateApptInput,
    { prev?: Appointment[]; tempId: string }
  >({
    mutationFn: async (input) => {
      const body: CreateAppointment = {
        ...input,
        idempotencyKey: input.idempotencyKey ?? crypto.randomUUID(),
      };
      try {
        return unwrap(await api.POST('/v1/appointments', { body }));
      } catch (err) {
        if (!(err instanceof ApiClientError)) {
          // Offline / network blip: queue it and reconcile in the background. Never block the desk.
          await enqueue('appointment.create', body, [key]);
          return null;
        }
        throw err;
      }
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Appointment[]>(key);
      const tempId = `${TEMP_PREFIX}${crypto.randomUUID()}`;
      qc.setQueryData<Appointment[]>(key, [...(prev ?? []), optimisticAppt(input, tempId)]);
      return { prev, tempId };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save the appointment');
    },
    onSuccess: (created, _input, ctx) => {
      if (created && ctx) {
        qc.setQueryData<Appointment[]>(key, (cur) =>
          (cur ?? []).map((a) => (a.id === ctx.tempId ? created : a)),
        );
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export interface UpdateApptInput {
  id: string;
  body: UpdateAppointment;
}

export function useUpdateAppointment(date: string) {
  const qc = useQueryClient();
  const { enqueue } = useOffline();
  const toast = useToast();
  const key = qk.appointments(date);

  return useMutation<Appointment | null, unknown, UpdateApptInput, { prev?: Appointment[] }>({
    mutationFn: async ({ id, body }) => {
      try {
        return unwrap(await api.PATCH('/v1/appointments/{id}', { params: { path: { id } }, body }));
      } catch (err) {
        if (!(err instanceof ApiClientError)) {
          await enqueue('appointment.update', { id, body }, [key]);
          return null;
        }
        throw err;
      }
    },
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Appointment[]>(key);
      qc.setQueryData<Appointment[]>(key, (cur) =>
        (cur ?? []).map((a) =>
          a.id === id
            ? {
                ...a,
                ...(body.staffId !== undefined ? { staffId: body.staffId } : {}),
                ...(body.startAt !== undefined ? { startAt: body.startAt } : {}),
                ...(body.endAt != null ? { endAt: body.endAt } : {}),
                ...(body.status !== undefined ? { status: body.status } : {}),
                ...(body.notes !== undefined ? { notes: body.notes ?? null } : {}),
                ...(body.clientDisplayName !== undefined
                  ? { clientDisplayName: body.clientDisplayName }
                  : {}),
                updatedAt: new Date().toISOString(),
              }
            : a,
        ),
      );
      return { prev };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update the appointment');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
